import { useEffect, useState } from "react";
import { cn } from "../utils/cn";
import type { BadgeTier } from "../types/common";

export type { BadgeTier };

export interface BadgePillProps {
  tier: BadgeTier;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

// ── Reactive dark-mode hook ───────────────────────────────────────────────────
// Watches the `dark` class on <html> via MutationObserver so the badge
// correctly switches bg colors without coupling to ThemeProvider.

function useIsDark(): boolean {
  const [isDark, setIsDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  return isDark;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function MountainIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 20" fill="currentColor" aria-hidden="true">
      <path d="M1 18 L7 6 L11 12 L15 4 L23 18 Z" />
    </svg>
  );
}

function StarIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function CrownIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 18h18v2H3zM2 8l4 7h12l4-7-5 3-5-7-5 7-5-3z" />
    </svg>
  );
}

function DashIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="5" y="11" width="14" height="2" rx="1" />
    </svg>
  );
}

// ── Tier config ───────────────────────────────────────────────────────────────

const TIER_CONFIG = {
  none: {
    label:     "Unverified",
    bg:        "#E5E7EB",
    bgDark:    "#374151",
    iconColor: "#9CA3AF",
    textColor: "#6B7280",
    iconColorDark: "#9CA3AF",
    textColorDark: "#D1D5DB",
    shadow:    "rgba(0,0,0,0.12)",
    icon:      (s: number) => <DashIcon size={s} />,
  },
  bronze: {
    label:     "Bronze",
    bg:        "#FEF3C7",
    bgDark:    "#78350F",
    iconColor: "#B45309",
    textColor: "#92400E",
    iconColorDark: "#FCD34D",
    textColorDark: "#FDE68A",
    shadow:    "rgba(180,83,9,0.28)",
    icon:      (s: number) => <MountainIcon size={s} />,
  },
  silver: {
    label:     "Silver",
    bg:        "#F1F5F9",
    bgDark:    "#1E293B",
    iconColor: "#475569",
    textColor: "#334155",
    iconColorDark: "#94A3B8",
    textColorDark: "#CBD5E1",
    shadow:    "rgba(71,85,105,0.24)",
    icon:      (s: number) => <MountainIcon size={s} />,
  },
  gold: {
    label:     "Gold",
    bg:        "#FEF9C3",
    bgDark:    "#713F12",
    iconColor: "#D97706",
    textColor: "#92400E",
    iconColorDark: "#FCD34D",
    textColorDark: "#FDE68A",
    shadow:    "rgba(217,119,6,0.32)",
    icon:      (s: number) => <StarIcon size={s} />,
  },
  elite: {
    label:     "Elite",
    bg:        "#4C1D95",
    bgDark:    "#3B0764",
    iconColor: "#E9D5FF",
    textColor: "#F5F3FF",
    iconColorDark: "#E9D5FF",
    textColorDark: "#F5F3FF",
    shadow:    "rgba(91,33,182,0.45)",
    icon:      (s: number) => <CrownIcon size={s} />,
  },
} as const;

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE = {
  sm: { width: 40,  height: 50,  pt: 6,  iconSize: 13, fontSize: 7  },
  md: { width: 52,  height: 65,  pt: 9,  iconSize: 17, fontSize: 9  },
  lg: { width: 68,  height: 85,  pt: 12, iconSize: 22, fontSize: 11 },
} as const;

// Shield clip-path: rectangular top + downward point at 68%
const SHIELD_CLIP = "polygon(0 0, 100% 0, 100% 68%, 50% 100%, 0 68%)";

// ── Component ─────────────────────────────────────────────────────────────────

export function BadgePill({
  tier,
  size = "md",
  showLabel = true,
  className,
}: BadgePillProps) {
  const cfg   = TIER_CONFIG[tier];
  const s     = SIZE[size];
  const isDark = useIsDark();

  const bg        = isDark ? cfg.bgDark    : cfg.bg;
  const iconColor = isDark ? cfg.iconColorDark : cfg.iconColor;
  const textColor = isDark ? cfg.textColorDark : cfg.textColor;

  return (
    <div
      className={cn("relative inline-flex select-none", className)}
      style={{ filter: `drop-shadow(0 2px 4px ${cfg.shadow})` }}
      role="img"
      aria-label={`${cfg.label} partner badge`}
    >
      {/* Shield body */}
      <div
        className="relative overflow-hidden"
        style={{
          width:    s.width,
          height:   s.height,
          clipPath: SHIELD_CLIP,
          background: bg,
        }}
      >
        {/* Gold: subtle radial inner glow */}
        {tier === "gold" && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 40%, rgba(251,191,36,0.35) 0%, transparent 70%)",
            }}
          />
        )}

        {/* Elite: shimmer sweep overlay */}
        {tier === "elite" && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none elite-shimmer-bg opacity-70"
          />
        )}

        {/* Silver: metallic sheen */}
        {tier === "silver" && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background: isDark
                ? "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 50%, rgba(255,255,255,0.06) 100%)"
                : "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, transparent 50%, rgba(255,255,255,0.2) 100%)",
            }}
          />
        )}

        {/* Content area — stays within the rectangular 68% */}
        <div
          className="flex flex-col items-center justify-center gap-0.5"
          style={{
            height:     `${s.height * 0.68}px`,
            paddingTop: s.pt,
          }}
        >
          <span style={{ color: iconColor, lineHeight: 1 }}>
            {cfg.icon(s.iconSize)}
          </span>

          {showLabel && (
            <span
              className="font-black uppercase tracking-widest leading-none"
              style={{ color: textColor, fontSize: s.fontSize }}
            >
              {cfg.label}
            </span>
          )}
        </div>

        {/* Inner edge highlight for depth */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            clipPath: SHIELD_CLIP,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.1)",
          }}
        />
      </div>
    </div>
  );
}

BadgePill.displayName = "BadgePill";
